import { CopyOutlined } from "@ant-design/icons";
import { App as AntdApp, Button, Tooltip } from "antd";
import type { ReactNode } from "react";

export interface CopyUrlButtonProps {
  url?: string;
  tooltip?: ReactNode;
  successMessage?: string;
  errorMessage?: string;
}

const copyText = async (value: string) => {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return true;
  }
  if (typeof document === "undefined") {
    return false;
  }
  const el = document.createElement("textarea");
  el.value = value;
  el.style.position = "fixed";
  el.style.insetInlineStart = "-9999px";
  el.style.top = "0";
  document.body.appendChild(el);
  el.focus();
  el.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(el);
  return ok;
};

const toAbsoluteUrl = (url: string) => {
  if (url.startsWith("http")) {
    return url;
  }
  if (typeof window === "undefined") {
    return url;
  }
  return `${window.location.origin}${url.startsWith("/") ? url : `/${url}`}`;
};

const CopyUrlButton = ({
  url,
  tooltip = "Копировать ссылку",
  successMessage = "Ссылка скопирована",
  errorMessage = "Не удалось скопировать ссылку",
}: CopyUrlButtonProps) => {
  const { message } = AntdApp.useApp();

  if (!url) {
    return null;
  }

  const handleCopy = async () => {
    const absolute = toAbsoluteUrl(url);
    try {
      const ok = await copyText(absolute);
      if (ok) {
        message.success(successMessage);
        return;
      }
      message.error(errorMessage);
    } catch {
      message.error(errorMessage);
    }
  };

  return (
    <Tooltip title={tooltip}>
      <Button
        aria-label="Копировать ссылку"
        icon={<CopyOutlined />}
        onClick={handleCopy}
        style={{
          width: 32,
          height: 32,
          paddingInline: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      />
    </Tooltip>
  );
};

export default CopyUrlButton;
